import React, { useState, useContext, useMemo } from 'react';
import { Select, Row, Col, Card, Progress, Typography, Spin, Empty, Pagination } from 'antd';
import { TrophyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { AppContext } from 'context/AppContext';
import { useJoinedCampaigns } from 'services/EnrollmentService';
import { Enrollment } from 'models/CommonType';

import './MyCampaignsList.css';

const { Text, Title } = Typography;
const { Option } = Select;

// Sorting options mapping - exactly 5 as specified
const sortingOptions = [
  { key: 'sorting.hot_down', value: '-campaign__campaign_order' },
  { key: 'sorting.start_time_up', value: 'campaign__started' },
  { key: 'sorting.start_time_down', value: '-campaign__started' },
  { key: 'sorting.expire_time_up', value: 'campaign__expired' },
  { key: 'sorting.expire_time_down', value: '-campaign__expired' },
];

// Filter options
const filterOptions = [
  { key: 'in_progress', value: false },
  { key: 'completed', value: true },
];

interface MyCampaignsListProps {
  className?: string;
}

const MyCampaignsList: React.FC<MyCampaignsListProps> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authentication, commonData, api } = useContext(AppContext);
  const { authKey } = authentication;
  const { userDetails } = commonData;
  
  const [sortBy, setSortBy] = useState('sorting.hot_down');
  const [filterBy, setFilterBy] = useState('in_progress');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Get user details if not available
  const { data: userDetailsResponse } = useSWR(
    authKey && !userDetails ? 'user-details' : null,
    () => api.profile.getUserDetails(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const currentUser = userDetails || (userDetailsResponse?.error ? null : (userDetailsResponse?.results?.[0] || userDetailsResponse));

  // Get the actual sort value from the selected key
  const actualSortValue = useMemo(() => {
    const option = sortingOptions.find(opt => opt.key === sortBy);
    return option?.value || '-campaign__campaign_order';
  }, [sortBy]);

  // Get the actual filter value from the selected key
  const actualFilterValue = useMemo(() => {
    const option = filterOptions.find(opt => opt.key === filterBy);
    return option?.value;
  }, [filterBy]);

  // Get filtered campaigns data
  const { data: enrollmentsData, isLoading } = useJoinedCampaigns(
    currentUser?.id || '',
    actualSortValue,
    page,
    actualFilterValue, // isExpired filter
    authKey
  );

  // Get all campaigns data to check if user has any joined campaigns at all
  const { data: allCampaignsData, isLoading: allCampaignsLoading } = useJoinedCampaigns(
    currentUser?.id || '',
    actualSortValue,
    1, // Always first page for checking
    null, // No filter to get all campaigns
    authKey
  );

  const enrollments = enrollmentsData?.results || [];
  const hasAnyJoinedCampaigns = !allCampaignsLoading && allCampaignsData?.results && allCampaignsData.results.length > 0;
  const isFilteredResult = true; // Always filtered now since we removed "all" option

  const handleSortChange = (value: unknown) => {
    setSortBy(value as string);
    setPage(1); // Reset to first page when sorting changes
  };

  const handleFilterChange = (e: any) => {
    setFilterBy(e.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleCampaignClick = (enrollment: Enrollment) => {
    if (enrollment.campaign?.id) {
      navigate(`/campaign?id=${enrollment.campaign.id}`);
    }
  };

  const getProgressPercentage = (enrollment: Enrollment): number => {
    const progress = enrollment.progress || 0;
    return Math.round(progress * 100);
  };

  if (!authKey || !currentUser) {
    return null;
  }

  // Always show filters, only show loading in content area
  const renderCampaignContent = () => {
    if (isLoading) {
      return (
        <div className="loading-container">
          <Spin size="large" />
          <div className="loading-text">Loading campaigns...</div>
        </div>
      );
    }

    if (enrollments.length === 0) {
      return (
        <div className="empty-container">
          <Empty 
            description={
              !hasAnyJoinedCampaigns 
                ? t('#Home.no_joined_campaigns')
                : isFilteredResult 
                  ? t('#Home.no_campaigns_for_filter')
                  : t('#Home.no_joined_campaigns')
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      );
    }

    return (
      <>
        <Row gutter={[24, 24]} className="my-campaigns-row">
          {enrollments.map((enrollment) => (
            <Col xs={24} sm={24} md={12} lg={8} xl={8} key={enrollment.id}>
              <Card 
                className="campaign-card"
                hoverable
                onClick={() => handleCampaignClick(enrollment)}
              >
                <div className="campaign-content">
                  <div className="campaign-header">
                    <div className="campaign-image-container">
                      {enrollment.campaign?.image_url ? (
                        <img 
                          className="campaign-image"
                          src={enrollment.campaign.image_url}
                          alt={enrollment.campaign.name}
                          onError={(e) => {
                            const placeholder = document.createElement('div');
                            placeholder.className = 'campaign-image-placeholder';
                            e.currentTarget.parentNode?.replaceChild(placeholder, e.currentTarget);
                          }}
                        />
                      ) : (
                        <div className="campaign-image-placeholder"></div>
                      )}
                    </div>
                    
                    <div className="campaign-header-info">
                      <Title level={4} className="campaign-title">
                        {enrollment.campaign?.name}
                      </Title>
                      
                      <Text className="organizer-name">
                        {t('#Campaign.by')} {enrollment.campaign?.user?.first_name} {enrollment.campaign?.user?.last_name}
                      </Text>
                    </div>
                  </div>

                  <div className="campaign-body">
                    <div className="stats-grid">
                      <div className="stat-card">
                        <div className="stat-icon">
                          <CheckCircleOutlined />
                        </div>
                        <div className="stat-content">
                          <div className="stat-value">{getProgressPercentage(enrollment)}%</div>
                          <div className="stat-label">{t('#Campaign.progress')}</div>
                        </div>
                      </div>
                      
                      <div className="stat-card">
                        <div className="stat-icon">
                          <TrophyOutlined />
                        </div>
                        <div className="stat-content">
                          <div className="stat-value">{enrollment.miles || 0}</div>
                          <div className="stat-label">{t('common.miles')}</div>
                        </div>
                      </div>
                    </div>

                    <div className="progress-section">
                      <div className="progress-header">
                        <span className="progress-text">Campaign Progress</span>
                        <span className="progress-percentage">{getProgressPercentage(enrollment)}%</span>
                      </div>
                      <Progress 
                        percent={getProgressPercentage(enrollment)}
                        strokeColor={{
                          '0%': '#9BC53D',
                          '100%': '#7BA428',
                        }}
                        trailColor="#f0f0f0"
                        strokeWidth={6}
                        showInfo={false}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Pagination */}
        {enrollmentsData?.meta?.pagination && enrollmentsData.meta.pagination.pages > 1 && (
          <div className="pagination-container">
            <Pagination
              current={page}
              total={enrollmentsData.meta.pagination.total}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total}`
              }
              hideOnSinglePage
              simple={false}
            />
          </div>
        )}
      </>
    );
  };

  // Show filters first, then handle empty states
  const renderFilters = () => (
    <div className="filters-container">
      <div className="filter-row">
        <div className="filter-group">
          <span className="filter-label">{t('#Home.filter')}:</span>
          <div className="tab-filter-container">
            <div 
              className={`tab-filter-item ${filterBy === 'in_progress' ? 'active' : ''}`}
              onClick={() => handleFilterChange({ target: { value: 'in_progress' } })}
            >
              {t('#Home.filter_in_progress')}
            </div>
            <div 
              className={`tab-filter-item ${filterBy === 'completed' ? 'active' : ''}`}
              onClick={() => handleFilterChange({ target: { value: 'completed' } })}
            >
              {t('#Home.filter_completed')}
            </div>
          </div>
        </div>

        <div className="filter-group">
          <span className="filter-label">{t('#Home.sort_by')}:</span>
          <Select
            className="styled-select"
            value={sortBy}
            onChange={handleSortChange}
            style={{ minWidth: 180 }}
            size="middle"
          >
            {sortingOptions.map(option => (
              <Option key={option.key} value={option.key}>
                {t(`#Home.${option.key}`)}
              </Option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`my-campaigns-list ${className || ''}`}>
      {renderFilters()}
      {renderCampaignContent()}
    </div>
  );
};

export default MyCampaignsList;